/**
 * Citadel — Platform Feature Switches surface.
 *
 * Extracted from platform-ui/src/pages/settings/OperationsPage.tsx
 * (PlatformFeatureSwitchesSection) and retargeted to /v1/citadel/feature-flags.
 *
 * Sprint: SPR-26.O | BL-29-1790
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Switch from '@radix-ui/react-switch';
// Synthesis XII-5: Phosphor (thin) for prominent surface chrome (size-4/size-5);
// Heroicons micro (16/solid) for dense table-cell badges (size-3/size-3.5) where the
// hairline solid glyph reads more clearly than a stroked icon at small sizes.
import { Warning as AlertTriangle, ToggleLeft } from 'phosphor-react';
import {
  ExclamationTriangleIcon as MicroAlertTriangle,
  CheckCircleIcon as MicroCheckCircle,
  ArrowPathIcon as MicroSpinner,
} from '@heroicons/react/16/solid';
import {
    listPlatformFeatureFlags,
    setPlatformFeatureFlag,
    type PlatformFeatureFlag,
} from '@/lib/api';
import {
    BUILD_TIME_FEATURE_DEFAULTS,
    FEATURE_FLAG_KEYS,
    type FeatureFlagKey,
} from '@/lib/features';
import { useAccessTokenSync } from '@/lib/auth';

interface FlagRowState {
    value: boolean;
    description: string | null;
    submitting: boolean;
    error: string | null;
}

function humaniseFlagKey(key: string): string {
    const spaced = key.replace(/([A-Z0-9])/g, ' $1').trim();
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

type LoadState = 'loading' | 'ready' | 'error';

export default function FeatureSwitches() {
    // BL-29-2163: `ready` flips true only once BOTH the Auth0 bearer token
    // and the tenant id are resolved. The initial refresh() is gated on it
    // so the first /v1/citadel/feature-flags call never races the async
    // token+tenant fetch (which would 401 tokenless / 400 tenantless).
    const { ready } = useAccessTokenSync();

    const [loadState, setLoadState] = useState<LoadState>('loading');
    const [loadError, setLoadError] = useState<string | null>(null);

    const initialRows = useMemo<Record<string, FlagRowState>>(() => {
        const seed: Record<string, FlagRowState> = {};
        for (const k of FEATURE_FLAG_KEYS) {
            seed[k] = {
                value: BUILD_TIME_FEATURE_DEFAULTS[k],
                description: null,
                submitting: false,
                error: null,
            };
        }
        return seed;
    }, []);
    const [rows, setRows] = useState<Record<string, FlagRowState>>(initialRows);

    const mountedRef = useRef(true);
    useEffect(() => {
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const refresh = useCallback(async () => {
        setLoadState('loading');
        setLoadError(null);
        try {
            const res = await listPlatformFeatureFlags();
            if (!mountedRef.current) return;
            const overrideMap = new Map<string, PlatformFeatureFlag>(
                res.flags.map((f) => [f.key, f]),
            );
            setRows((prev) => {
                const next: Record<string, FlagRowState> = { ...prev };
                for (const k of FEATURE_FLAG_KEYS) {
                    const override = overrideMap.get(k);
                    next[k] = {
                        value: override ? override.value : BUILD_TIME_FEATURE_DEFAULTS[k],
                        description: override ? override.description : null,
                        submitting: false,
                        error: null,
                    };
                }
                return next;
            });
            setLoadState('ready');
        } catch (err) {
            if (!mountedRef.current) return;
            setLoadError(
                err instanceof Error ? err.message : 'Failed to load feature flags.',
            );
            setLoadState('error');
        }
    }, []);

    // Gate the initial load on API readiness. Until `ready` is true the
    // surface stays in its `loading` state (the default) — it must NOT
    // fall to the error state, which is reserved for genuine fetch
    // failures (BL-29-2163).
    useEffect(() => {
        if (!ready) return;
        void refresh();
    }, [ready, refresh]);

    const handleToggle = useCallback(
        async (key: FeatureFlagKey, nextValue: boolean) => {
            setRows((prev) => ({
                ...prev,
                [key]: { ...prev[key], value: nextValue, submitting: true, error: null },
            }));
            try {
                const updated = await setPlatformFeatureFlag(key, nextValue);
                if (!mountedRef.current) return;
                setRows((prev) => ({
                    ...prev,
                    [key]: {
                        value: updated.value,
                        description: updated.description,
                        submitting: false,
                        error: null,
                    },
                }));
            } catch (err) {
                if (!mountedRef.current) return;
                setRows((prev) => ({
                    ...prev,
                    [key]: {
                        ...prev[key],
                        value: !nextValue,
                        submitting: false,
                        error: err instanceof Error ? err.message : 'Toggle failed.',
                    },
                }));
            }
        },
        [],
    );

    return (
        <section
            className="space-y-4"
            aria-labelledby="feature-switches-heading"
            data-testid="feature-switches"
        >
            <header className="flex items-start justify-between gap-4">
                <div>
                    <div className="flex items-center gap-2">
                        <ToggleLeft className="size-4 text-citadel-cyan" weight="thin" aria-hidden />
                        <h1
                            id="feature-switches-heading"
                            className="text-lg font-semibold tracking-tight"
                        >
                            Feature switches
                        </h1>
                    </div>
                    <p className="mt-1 text-xs text-citadel-ink-dim leading-relaxed max-w-2xl">
                        Master controls for platform-wide features. A toggle is
                        persisted immediately; each platform-ui session picks up
                        the change on its next page load (overrides are fetched
                        once per session after sign-in). Every toggle is
                        forensics-logged with the actor subject + previous value.
                    </p>
                </div>
                <span className="text-2xs font-mono text-citadel-ink-faint shrink-0">
                    /v1/citadel/feature-flags
                </span>
            </header>

            {loadState === 'loading' && (
                <div
                    role="status"
                    className="flex items-center gap-2 border border-citadel-border bg-citadel-surface p-3 text-xs font-mono text-citadel-ink-dim"
                >
                    <MicroSpinner className="size-3.5 animate-spin" />
                    Loading feature flags...
                </div>
            )}

            {loadState === 'error' && (
                <div
                    role="alert"
                    className="flex items-start gap-2 border border-citadel-danger/40 bg-citadel-danger/10 p-3"
                >
                    <AlertTriangle className="size-4 text-citadel-danger mt-0.5 shrink-0" weight="thin" />
                    <div className="text-xs">
                        <p className="font-semibold text-citadel-danger">
                            Failed to load feature flags.
                        </p>
                        <p className="mt-1 text-citadel-ink-dim">{loadError}</p>
                        <button
                            type="button"
                            onClick={() => void refresh()}
                            className="mt-2 text-2xs font-mono uppercase tracking-wider px-2 py-1 border border-citadel-danger/40 text-citadel-danger hover:bg-citadel-danger/10"
                        >
                            Retry
                        </button>
                    </div>
                </div>
            )}

            {loadState === 'ready' && (
                <ul className="space-y-1.5" data-testid="feature-switches-list">
                    {FEATURE_FLAG_KEYS.map((key) => {
                        const row = rows[key];
                        const defaultValue = BUILD_TIME_FEATURE_DEFAULTS[key];
                        const overridden = row.value !== defaultValue;
                        return (
                            <li
                                key={key}
                                data-testid={`feature-flag-row-${key}`}
                                className="border border-citadel-border bg-citadel-surface p-3"
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <h3 className="text-sm font-semibold text-citadel-ink">
                                                {humaniseFlagKey(key)}
                                            </h3>
                                            {overridden && (
                                                <span className="text-2xs font-mono uppercase tracking-wider px-1.5 py-0.5 bg-citadel-amber/10 text-citadel-amber">
                                                    Override
                                                </span>
                                            )}
                                        </div>
                                        <p className="mt-1 text-xs text-citadel-ink-dim leading-relaxed">
                                            {row.description ?? '—'}
                                        </p>
                                        <p className="mt-1 text-2xs font-mono uppercase tracking-wider text-citadel-ink-faint">
                                            Key: {key} · Default: {defaultValue ? 'ON' : 'OFF'}
                                        </p>
                                    </div>
                                    <div className="shrink-0 flex items-center gap-2">
                                        {row.submitting && (
                                            <MicroSpinner className="size-3.5 animate-spin text-citadel-ink-faint" />
                                        )}
                                        <Switch.Root
                                            checked={row.value}
                                            disabled={row.submitting}
                                            onCheckedChange={(next: boolean) =>
                                                void handleToggle(key, next)
                                            }
                                            aria-label={`Toggle ${key}`}
                                            data-testid={`feature-flag-toggle-${key}`}
                                            className="w-9 h-5 bg-citadel-border data-[state=checked]:bg-citadel-cyan transition-colors relative outline-none focus-visible:ring-2 focus-visible:ring-citadel-cyan/40"
                                        >
                                            <Switch.Thumb className="block size-4 bg-citadel-ink translate-x-0.5 data-[state=checked]:translate-x-[18px] transition-transform" />
                                        </Switch.Root>
                                    </div>
                                </div>
                                {row.error && (
                                    <div
                                        role="alert"
                                        className="mt-2 flex items-start gap-2 border border-citadel-danger/40 bg-citadel-danger/10 p-2"
                                        data-testid={`feature-flag-error-${key}`}
                                    >
                                        <MicroAlertTriangle className="size-3.5 text-citadel-danger mt-0.5 shrink-0" />
                                        <p className="text-xs text-citadel-ink-dim">{row.error}</p>
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}

            {loadState === 'ready' && (
                <div
                    aria-live="polite"
                    className="flex items-center gap-2 text-2xs font-mono text-citadel-ink-faint"
                >
                    <MicroCheckCircle className="size-3 text-citadel-success" aria-hidden />
                    Loaded {FEATURE_FLAG_KEYS.length} flags · defaults apply where no override row exists.
                </div>
            )}
        </section>
    );
}
